/*
 * Copyright (c) 2022. American Express Company
 */

// third-party
import _ from 'lodash';
import { Group } from 'konva/lib/Group';
import { Rect } from 'konva/lib/shapes/Rect';
import { RegularPolygon } from 'konva/lib/shapes/RegularPolygon';
// first-party
import { TLElement } from './TLElement';
import ProjectJaeger from '../ProjectJaeger';
import { CardModel } from 'src/types/card.model';
import TLPin from './TLPin';
import Konva from 'konva';
import CardService from '../../../services/card.service';
import { JaegerService } from '../../../services/jaeger.service';
import UnitService from '../../../services/unit.service';
import TimelineService from '../services/timeline.service.singleton';
import PinFactory from '../factories/pin.factory.singleton';
// styles
import colours from 'src/styles/variables.module.scss';

export default class TLPinboard extends TLElement {
    public parent: ProjectJaeger;
    public pinboardGroup: Group;
    public height: number;
    public width: number;
    public pins: TLPin[] = [];
    public animations: Konva.Animation[] = [];
    public cardUpdates: Array<Promise<CardModel>> = [];
    public dragFlag: Group;
    private readonly timelineService: TimelineService;
    private readonly pinFactory: PinFactory;

    constructor(parent: ProjectJaeger) {
        super();
        this.parent = parent;
        this.timelineService = TimelineService.getInstance();
        this.pinFactory = PinFactory.getInstance();
    }

    private setParams(): void {
        this.height = this.timelineService.getProps().height - this.timelineService.getTimeline().timelineY;
        this.width = this.timelineService.getStage().width();
    }

    private hideSavingsCards(cards: CardModel[]): CardModel[] {
        const savingsCards: number[] = [];
        return cards
            .map((card: CardModel) => {
                const linkedCardId: number = card.savingsCardDescription && _.get(card, 'attributes.linked_card_id');
                if (linkedCardId) {
                    const savingsCard = cards.find((card) => card.id === linkedCardId);
                    if (!savingsCard) return card;
                    card.percentSaved = savingsCard.percentSaved;
                    savingsCards.push(savingsCard.id);
                }

                return card;
            })
            .filter((card) => !savingsCards.includes(card.id));
    }

    public upsert(): void {
        this.setParams();
        if (this.pinboardGroup) {
            this.pinboardGroup.width(this.width);
            this.pinboardGroup.height(this.height);
            this.updatePins(this.timelineService.getProps().user.cards);
        } else {
            this.pinboardGroup = new Group({
                x: 0,
                y: 0,
                width: this.width,
                height: this.height,
                perfectDrawEnabled: false
            });
            this.addPins(this.timelineService.getProps().user.cards);

            this.timelineService.getMainLayer().add(this.pinboardGroup);
        }
    }

    public create(): void {
        this.upsert();
    }

    public getVisiblePins(): TLPin[] {
        return this.pins.filter((pin) => pin.pinGroup.isVisible());
    }

    public addDragFlag(x: number, y: number): void {
        if (!this.dragFlag) {
            this.dragFlag = new Group({
                x: x - 50,
                y: y - 42,
                width: 100,
                height: 42,
                perfectDrawEnabled: false
            });

            const rect: Konva.Rect = new Rect({
                x: 0,
                y: 0,
                width: 100,
                height: 32,
                fill: colours.dragFlagBox,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false,
                cornerRadius: 4
            });
            this.dragFlag.add(rect);

            const text: Konva.Text = new Konva.Text({
                x: 10,
                y: 1,
                text: 'Drag me',
                fontSize: 20,
                fontWeight: 700,
                lineHeight: 1.5,
                fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                fill: colours.dragFlagText,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });
            this.dragFlag.add(text);

            const triangle: Konva.RegularPolygon = new RegularPolygon({
                x: 50,
                y: 32,
                rotation: 180,
                sides: 3,
                radius: 5,
                fill: colours.dragFlagBox,
                perfectDrawEnabled: false,
                shadowForStrokeEnabled: false
            });
            this.dragFlag.add(triangle);

            this.pinboardGroup.add(this.dragFlag);
        }
    }

    public addPin(xPosition: number, yPosition: number, card: CardModel): void {
        if (!card) {
            console.error('Card not found');
            return;
        }

        if (!this.pinboardGroup) {
            console.error('Pinboard not found');
            return;
        }

        const reuseablePin: TLPin = this.pins.find((pin: TLPin) => pin.pinGroup.isVisible() === false);
        if (reuseablePin) {
            reuseablePin.upsert(xPosition, yPosition, card);
        } else {
            const pin: TLPin = this.pinFactory.create(xPosition, yPosition, card);
            pin.create();
            this.pinboardGroup.add(pin.pinGroup);
            this.pins.push(pin);
        }
    }

    public getPinPositionFromCard(card: CardModel): [number, number] {
        const rectSize: number = JaegerService.getPinSize(card);
        const y: number = JaegerService.getCardYCoordinate(
            card.yPosition || 0.5,
            rectSize,
            this.timelineService.getProps().height * this.timelineService.getProps().context.timelinePosition
        );
        const x: number = UnitService.getJaegerX(
            this.timelineService.getProps().context,
            this.timelineService.getProps().user,
            card.startDate
        );
        return [x, y];
    }

    public updatePins(cards: CardModel[]): void {
        const filteredCards: CardModel[] = this.hideSavingsCards(cards);
        filteredCards.forEach((card: CardModel) => {
            const [x, y]: [number, number] = this.getPinPositionFromCard(card);
            const pin = this.pins.find((pin) => pin.card.id === card.id);
            if (pin) {
                pin.upsert(x, y, card);
            } else {
                if (card.yPosition === 0) {
                    // no yPosition has been set, let it make a random one
                    card.yPosition = Math.random();
                    // push the promise so we can wait for them later
                    this.cardUpdates.push(CardService.updateCard(card, this.timelineService.getProps().user));
                }
                this.addPin(x, y, card);
            }
        });
        this.getVisiblePins().forEach((pin) => {
            // remove any orphan pins (like the temporary one when dropping a new goal)
            if (!pin.card || !pin.card.id || !cards.find((card) => card.id === pin.card.id)) {
                pin.pinGroup.hide();
                pin.line?.hide();
                pin.dot?.hide();
                pin.eventLengthRect?.hide();
            }
            if (pin.card.warnings?.length > 0) {
                pin.alertIcon?.show();
            } else {
                pin.alertIcon?.hide();
            }
        });

        if (!this.timelineService.getProps().dragFirstGoal && this.dragFlag) {
            this.dragFlag.hide();
        }

        // let the cards update in the background and move on
        Promise.all(this.cardUpdates).catch(console.error);
    }

    public addPins(cards: CardModel[]): void {
        const filteredCards: CardModel[] = this.hideSavingsCards(cards);
        this.pins.forEach((pin: TLPin) => pin.pinGroup.hide());
        filteredCards.forEach((card: CardModel) => {
            if (card.yPosition === 0) {
                // no yPosition has been set, let it make a random one
                card.yPosition = Math.random();
                // push the promise so we can wait for them later
                this.cardUpdates.push(CardService.updateCard(card, this.timelineService.getProps().user));
            }
            const [x, y]: [number, number] = this.getPinPositionFromCard(card);
            this.addPin(x, y, card);
        });

        if (this.timelineService.getProps().dragFirstGoal && filteredCards.length) {
            let dragGoal: CardModel = filteredCards[0];
            if (filteredCards.length > 1 && dragGoal.description === 'end of life') {
                dragGoal = filteredCards[1];
            }
            const [x, y]: [number, number] = this.getPinPositionFromCard(dragGoal);
            this.addDragFlag(x, y);
        } else {
            if (this.dragFlag) {
                this.dragFlag.hide();
            }
        }

        // let the cards update in the background and move on
        Promise.all(this.cardUpdates).catch(console.error);
    }

    public resetAnimation(): void {
        // handle change mid-animation
        if (this.animations && this.pins) {
            // stop old animations and reset opacity
            this.animations.forEach((anim) => anim.stop());
            this.pins.forEach((pin) => pin.pinGroup.opacity(1));
        }
        // start with clean slate
        this.animations = [];
    }

    public destroy(): void {
        if (!this.pinboardGroup) return;
        this.pinboardGroup.destroyChildren();
        this.pinboardGroup.destroy();
        this.pins.forEach((pin: TLPin) => pin.destroy());
        this.pins = [];
    }

    public remove(): void {}

    public updateX(): void {
        this.getVisiblePins().forEach((pin) => pin.updateX());
    }

    public updateY(): void {}
}
